"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC, HTMLProps } from "react";
import styled, { css } from "styled-components";
import { Link as NextLink } from "next-view-transitions";
import type { UrlObject } from "node:url";
import STYLES from "@vita-learning/styles";
import { isValidButtonType } from "./isValidButtonType";
export type ButtonProps = {
  readonly color?: string;
  readonly pseudoColor?: string;
  readonly ghost?: boolean;
} & (({
  as: "a";
  href: string | UrlObject;
} & HTMLProps<HTMLAnchorElement>) | ({
  as?: "button";
} & HTMLProps<HTMLButtonElement>));
export const Button: FC<ButtonProps> = t0 => {
  const $ = _c(14);
  let children;
  let color;
  let ghost;
  let props;
  let pseudoColor;
  let t1;
  let t2;
  if ($[0] !== t0) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        children: t3,
        color: t4,
        pseudoColor: t5,
        ghost: t6,
        ...t7
      } = t0;
      children = t3;
      color = t4;
      ghost = t6;
      props = t7;
      pseudoColor = t5 === undefined ? color : t5;
      if (props.as === "a") {
        t2 = <LinkBase {...props} $color={color} $pseudoColor={pseudoColor} $ghost={ghost}>{children}</LinkBase>;
        break bb0;
      }
      t1 = isValidButtonType(props.type);
    }
    $[0] = t0;
    $[1] = children;
    $[2] = color;
    $[3] = ghost;
    $[4] = props;
    $[5] = pseudoColor;
    $[6] = t1;
    $[7] = t2;
  } else {
    children = $[1];
    color = $[2];
    ghost = $[3];
    props = $[4];
    pseudoColor = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  if (!t1) {
    throw new Error("Button type is not a valid type");
  }
  let t3;
  if ($[8] !== children || $[9] !== color || $[10] !== ghost || $[11] !== props || $[12] !== pseudoColor) {
    t3 = <ButtonBase {...props} type={props.type} $color={color} $pseudoColor={pseudoColor} $ghost={ghost}>{children}</ButtonBase>;
    $[8] = children;
    $[9] = color;
    $[10] = ghost;
    $[11] = props;
    $[12] = pseudoColor;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
interface ButtonStyledProps {
  /**
   * The button's main color.
   * @default STYLES.colors.vitas.primary
   */
  readonly $color?: string;
  /**
   * The button's color on hover/focus/active states.
   * @default STYLES.colors.vitas.secondary
   */
  readonly $pseudoColor?: string;
  /**
   * Whether the button should have a "ghost" style (transparent background).
   */
  readonly $ghost?: boolean;
}
const {
  primary,
  secondary
} = STYLES.colors.vitas;
export const buttonCss = css<ButtonStyledProps>`
	align-items: center;
	background-color: ${({
  $ghost,
  $color = primary
}) => $ghost ? "transparent" : $color};
	border: 1px solid ${({
  $ghost,
  $color = primary
}) => $ghost ? $color : "transparent"};
	border-radius: 0.33rem;
	color: ${({
  $ghost,
  $color = primary
}) => $ghost ? $color : "white"};
	cursor: pointer;
	display: flex;
	outline: none;
	justify-content: center;
	padding: 0.25rem 0.5rem;
	transition: background-color 0.5s ease;

	&:hover,
	&:focus,
	&:active {
		background-color: ${({
  $ghost,
  $pseudoColor = secondary
}) => $ghost ? "transparent" : $pseudoColor};
	}

	&:disabled {
		cursor: default;
		opacity: 0.75;

		&:hover {
			background-color: ${({
  $ghost,
  $color = primary
}) => $ghost ? "transparent" : $color};
		}
	}
	
	&& {
		svg {
      color: ${({
  $ghost,
  $color = primary
}) => $ghost ? $color : "white"};
		}
	}

	@media screen and (${STYLES.breakpoints.desktop}) {
		border-radius: 0.5rem;
		font-size: 1.5rem;
		padding: 0.4rem 0.75rem;
	}
`;
const ButtonBase = styled.button<ButtonStyledProps>`
	${buttonCss}
`;
const LinkBase = styled(NextLink)<ButtonStyledProps>`
	${buttonCss}
	text-decoration: none;
`;
export default Button;